import { Button, Modal, Typography } from '@automata/ui';
import { isLocationInput } from '@automata/wfb-api';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { TaskRow } from './TaskRow';
import {
  selectWorkflowJsonToPreview,
  selectWorkflowJsonToSave,
} from '../../../../features/workflowJsonSelectors';
import { useAppSelector } from '../../../../hooks';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const InfoTable = ({ open, onClose }: Props) => {
  const workflowJsonToPreview = useAppSelector(selectWorkflowJsonToPreview);
  const workflowJsonToSave = useAppSelector(selectWorkflowJsonToSave);

  const allTasksToSave = workflowJsonToSave.workflow.tasks;

  const excludedLabware =
    'data' in workflowJsonToPreview
      ? workflowJsonToSave.workflow.labware.filter(
          (labware) =>
            !workflowJsonToPreview.data.workflow.labware.find(
              (l) => l.id === labware.id
            )
        )
      : null;

  const excludedTasks =
    'data' in workflowJsonToPreview
      ? Object.values(allTasksToSave).filter(
          (task) => !workflowJsonToPreview.data.workflow.tasks[task.id]
        )
      : null;

  return (
    <Modal open={open} onClose={onClose} title="Info">
      <Stack gap={5}>
        <Box>
          <Typography variant="h6">
            Labware ({workflowJsonToSave.workflow.labware.length})
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="none">ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Slot</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflowJsonToSave.workflow.labware.map(
                  (labware) =>
                    isLocationInput(labware.location) && (
                      <TableRow key={labware.id}>
                        <TableCell padding="none">{labware.id}</TableCell>
                        <TableCell>{labware.description}</TableCell>
                        <TableCell>{labware.location.instrument}</TableCell>
                        <TableCell>{labware.location.slot}</TableCell>
                        <TableCell>{labware.type}</TableCell>
                      </TableRow>
                    )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box>
          <Typography variant="h6">
            Labware excluded from preview (
            {excludedLabware ? excludedLabware.length : 'all'})
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="none">ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Slot</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {excludedLabware?.map(
                  (labware) =>
                    isLocationInput(labware.location) && (
                      <TableRow key={labware.id}>
                        <TableCell padding="none">{labware.id}</TableCell>
                        <TableCell>{labware.description}</TableCell>
                        <TableCell>{labware.location.instrument}</TableCell>
                        <TableCell>{labware.location.slot}</TableCell>
                        <TableCell>{labware.type}</TableCell>
                      </TableRow>
                    )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box>
          <Typography variant="h6">Tasks</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="none">ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Dependencies</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.values(allTasksToSave).map((task) => (
                  <TaskRow
                    key={task.id}
                    allTasks={allTasksToSave}
                    task={task}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box>
          <Typography variant="h6">
            Tasks excluded from preview (
            {excludedTasks ? excludedTasks.length : 'all'})
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="none">ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Dependencies</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {excludedTasks?.map((task) => (
                  <TaskRow
                    key={task.id}
                    allTasks={allTasksToSave}
                    task={task}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Button
          variant="outlined"
          onClick={onClose}
          type="button"
          sx={{ flexGrow: 1 }}
        >
          Close
        </Button>
      </Stack>
    </Modal>
  );
};
